import React, { useState, useEffect } from "react";
import { Modal, Button, TextInput, SelectDropdown } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classes from "./Reminder.scss";
import classNames from "classnames";
import {
  getCustomScheduleOptions,
  getCustomScheduleOptionsForAllDay,
} from "../../modules/Utils";
import _ from "lodash";

const getMinutes = (value = 0, timeType) => {
  switch (timeType) {
    case "weeks":
      return value * 7 * 24 * 60; // Convert weeks to minutes
    case "days":
      return value * 24 * 60; // Convert days to minutes
    case "hours":
      return value * 60; // Convert hours to minutes
    case "minutes":
      return value; // Already in minutes
    default:
      return 0;
  }
};

const convertMinutesToWeekDaysHoursMinutesObject = minutes => {
  const weeks = Math.floor(minutes / (7 * 24 * 60));
  minutes -= weeks * 7 * 24 * 60;
  const days = Math.floor(minutes / (24 * 60));
  minutes -= days * 24 * 60;
  const hours = Math.floor(minutes / 60);
  minutes -= hours * 60;
  return {
    weeks,
    days,
    hours,
    minutes,
  };
};

const MAX_ALLOWED_REMIDER_DAYS = 28; // Maximum allowed reminder days

/**
 * Check if the calculated custom time conflicts with existing reminders
 * @param {number} inputValue - The numeric input value
 * @param {string} timeType - The time type (minutes, hours, days, weeks)
 * @param {Array} existingReminderTimes - Array of existing reminder times
 * @returns {boolean} - True if there's a conflict
 */
const hasConflictWithExisting = (
  inputValue,
  timeType,
  existingReminderTimes
) => {
  const calculatedMinutes = getMinutes(inputValue, timeType);
  return _.includes(existingReminderTimes, calculatedMinutes);
};

const CustomModal = ({
  isOpen,
  onClose,
  defaultValue = null,
  onCustomValueChange,
  testId = "ReminderCustomModal",
  existingReminderTimes = [], // Array of already selected schedule times
  isAllDayEvent = false,
}) => {
  const { t } = useI18n(["calendar"]);

  const maxValues = MAX_ALLOWED_REMIDER_DAYS * 24 * 60; // 28 days in minutes

  const schduleOptions = isAllDayEvent
    ? getCustomScheduleOptionsForAllDay(t)
    : getCustomScheduleOptions(t);
  const [selectedSchedule, setSelectedSchedule] = useState(schduleOptions[0]);

  const onChangeScheduleType = selectedOption => {
    const hasMaxValueError =
      getMinutes(value, selectedOption?.value) > maxValues;
    const hasConflictError = hasConflictWithExisting(
      value,
      selectedOption?.value,
      existingReminderTimes
    );

    if (hasMaxValueError || hasConflictError) {
      setError(hasMaxValueError ? "max_value" : "conflict");
    } else {
      setError(false);
    }
    setSelectedSchedule(selectedOption);
  };

  const [value, setValue] = useState(defaultValue || 5);
  const [error, setError] = useState(false);

  // Check for conflicts when the modal opens or dependencies change
  useEffect(() => {
    const hasMaxValueError =
      getMinutes(value, selectedSchedule?.value) > maxValues;
    const hasConflictError = hasConflictWithExisting(
      value,
      selectedSchedule?.value,
      existingReminderTimes
    );

    if (hasMaxValueError || hasConflictError) {
      setError(hasMaxValueError ? "max_value" : "conflict");
    } else {
      setError(false);
    }
  }, [value, selectedSchedule, existingReminderTimes, maxValues]);

  const onChangeHandler = event => {
    const inputValue = event.target.value;
    // Convert to positive number and validate
    const numericValue = Math.abs(parseInt(inputValue, 10));

    const hasMaxValueError =
      getMinutes(numericValue, selectedSchedule?.value) > maxValues;
    const hasConflictError = hasConflictWithExisting(
      numericValue,
      selectedSchedule?.value,
      existingReminderTimes
    );

    if (hasMaxValueError || hasConflictError) {
      setError(hasMaxValueError ? "max_value" : "conflict");
    } else {
      setError(false);
    }

    if (!isNaN(numericValue)) {
      setValue(numericValue);
    } else if (inputValue === "") {
      setValue("");
    }
  };

  const onSubmit = () => {
    onCustomValueChange(
      convertMinutesToWeekDaysHoursMinutesObject(
        getMinutes(value, selectedSchedule?.value)
      )
    );
    onClose && onClose();
  };

  const cancelHandler = () => {
    onClose && onClose();
  };

  const getMaxReminderTime = unit => {
    switch (unit) {
      case "weeks":
        return MAX_ALLOWED_REMIDER_DAYS / 7; // 4 weeks
      case "days":
        return MAX_ALLOWED_REMIDER_DAYS; // 28 days
      case "hours":
        return MAX_ALLOWED_REMIDER_DAYS * 24; // 28 days in hours
      case "minutes":
        return MAX_ALLOWED_REMIDER_DAYS * 24 * 60; // 28 days in minutes
      default:
        return 0;
    }
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      testId="custom-modal"
      height="max-content"
      width="480px"
    >
      <Modal.Header>{t("calendar:custom_reminder_for_event")}</Modal.Header>
      <Modal.Body>
        <div className={classes.inputContainer}>
          <TextInput
            value={value}
            onChange={onChangeHandler}
            placeholder={t("calendar:enter_value")}
          />
          <SelectDropdown
            placeholder={t("calendar:select_schedule_type")}
            options={schduleOptions}
            value={selectedSchedule}
            onChange={onChangeScheduleType}
            isMulti={false}
            isClearable={false}
            size="medium"
            testId={`${testId}-reminderTime`}
            dsVersion="2.0"
          />
        </div>
        {error ? (
          <div className={classes.error}>
            {error === "max_value"
              ? t("calendar:max_day_error", {
                  time: getMaxReminderTime(selectedSchedule?.value),
                  unit: selectedSchedule?.label?.toLowerCase(),
                })
              : error === "conflict"
              ? t("calendar:reminder_time_already_selected")
              : null}
          </div>
        ) : null}
        {value && !error ? (
          <div className={classNames(classes.details, "text-body")}>
            {t("calendar:user_will_be_notified", {
              time: t(
                `calendar:count_${selectedSchedule?.value?.toLowerCase()}`,
                {
                  count: Number(value),
                }
              ),
            })}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.actionButton}>
          <Button
            variant="neutral"
            type="outlined"
            onClick={cancelHandler}
            testId={`${testId}-cancel-button`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={error || value <= 0}
            testId={`${testId}-confirm-button`}
          >
            {t("calendar:add_reminder")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomModal;
